//#region imports
import React, { useState, useEffect } from 'react';

// bootstrap
import { Container, Row, Col, Card, Button, Alert, Form } from 'react-bootstrap';

// redux
import { useDispatch } from 'react-redux';
import { confirmPasswordChanged } from '_redux/slices/profile/staff-management';

// firebase
import fire from '_utils/firebase';

// styling
import { Colors } from '_styles';

// components
import LogoutButton from '_components/atoms/button/logout-button';

//#endregion

const PasswordReset = () => {

  //#region local states
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'danger' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  //#endregion

  // firebase auth
  const currentUser = fire.auth().currentUser;

  // redux
  const dispatch = useDispatch();

  // function to handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setAlert({ show: true, message: 'No authenticated user found. Please log in first.', variant: 'danger' });
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      setAlert({ show: true, message: 'Password must be at least 8 characters long.', variant: 'danger' });
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setAlert({ show: true, message: 'Password must contain at least one uppercase letter.', variant: 'danger' });
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setAlert({ show: true, message: 'Password must contain at least one number.', variant: 'danger' });
      return;
    }

    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setAlert({ show: true, message: 'Password must contain at least one symbol (e.g. !@#$%^&*).', variant: 'danger' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setAlert({ show: true, message: 'Passwords do not match.', variant: 'danger' });
      return;
    }

    setLoading(true);

    try {
      // update password in Firebase
      await currentUser.updatePassword(newPassword);

      // a password change revokes every session signed in before it, judged by sign-in time,
      // so refreshing the token isn't enough. sign in again with the new password to get a
      // session the backend accepts. onboarding sets the password before MFA, so no second factor yet
      try {
        const credential = fire.auth.EmailAuthProvider.credential(currentUser.email, newPassword);
        await currentUser.reauthenticateWithCredential(credential);
      } catch (reauthError) {
        console.warn('Re-authentication after password change failed:', reauthError.code);
      }

      // tell the backend so it clears the tempPassword custom claim
      await dispatch(confirmPasswordChanged()).unwrap();

      setAlert({ show: true, message: 'Password changed successfully! Continuing...', variant: 'success' });

      // force token refresh to get the updated custom claims
      await currentUser.getIdToken(true);

      // reload to let the root router move to the next onboarding step
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
    catch (error) {
      console.error('Error changing password:', error);

      let errorMessage = 'Failed to change password. Please try again.';
      if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log out and log back in before changing your password.';
      }

      setAlert({ show: true, message: errorMessage, variant: 'danger' });
      setLoading(false);
    }
  };

  // function to handle logout error
  const handleLogoutError = () => {
    setAlert({ show: true, message: 'Error during logout. Please try again.', variant: 'danger' });
  };

  // show alert for 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ show: false, message: '', variant: 'danger' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  return (
    <Container fluid className="d-flex align-items-center justify-content-center min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <Row className="w-100 justify-content-center">
        <Col xs={12} md={8} lg={6} xl={4}>
          <Card className="shadow-sm">
            <Card.Body className="p-5">

              {/* title */}
              <div className="text-center mb-4">
                <h2 style={{ color: Colors.PRM_LIGHT_BLUE, fontWeight: 'bold' }}>
                  Change Temporary Password
                </h2>
                <p className="text-muted">
                  Create a secure password to replace your temporary password
                </p>
              </div>

              {alert.show && (
                <Alert variant={alert.variant} className="mb-3">
                  {alert.message}
                </Alert>
              )}

              {currentUser && (
                <div className="text-center mb-4">
                  <p className="text-muted">
                    Account: <strong>{currentUser.email}</strong>
                  </p>
                </div>
              )}

              <Form onSubmit={handleChangePassword}>
                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter your new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={loading}
                    minLength={8}
                  />
                  <Form.Text className="text-muted">
                    Password must be at least 8 characters and include an uppercase letter, a number, and a symbol
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    minLength={8}
                  />
                </Form.Group>

                <Button
                  type="submit"
                  className="w-100 mb-3"
                  style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, borderColor: Colors.PRM_LIGHT_BLUE }}
                  disabled={loading}
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </Form>

              {/* Logout Button */}
              <div className="text-center">
                <LogoutButton
                  disabled={loading}
                  onLogoutError={handleLogoutError}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PasswordReset;
